import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { SITE, siteUrl, resolveLang, type Lang } from '@/lib/site'

interface SeoProps {
  title?: string
  description?: string
  path: string
  type?: 'website' | 'article'
  image?: string
  publishedTime?: string
  noindex?: boolean
  jsonLd?: object | object[]
}

function fullUrl(lang: Lang, image: string | undefined): string {
  if (!image) return siteUrl(lang, SITE.defaultOgImage)
  if (/^https?:\/\//.test(image)) return image
  return siteUrl(lang, image)
}

export default function Seo({
  title,
  description,
  path,
  type = 'website',
  image,
  publishedTime,
  noindex = false,
  jsonLd,
}: SeoProps) {
  const { t, i18n } = useTranslation()
  const lang = resolveLang(i18n.resolvedLanguage)
  const tagline = t('seo.tagline') as string
  const fullTitle = title ? `${title} — ${SITE.name}` : `${SITE.name} — ${tagline}`
  const finalDescription = description ?? (t('seo.defaultDescription') as string)
  const canonical = siteUrl(lang, path)
  const altLang: Lang = lang === 'pt-BR' ? 'en' : 'pt-BR'
  const altUrl = siteUrl(altLang, path)
  const ogImage = fullUrl(lang, image)
  const ogLocale = lang === 'pt-BR' ? 'pt_BR' : 'en_US'
  const altLocale = lang === 'pt-BR' ? 'en_US' : 'pt_BR'

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const jsonLdArr = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang={lang} href={canonical} />
      <link rel="alternate" hrefLang={altLang} href={altUrl} />
      <link rel="alternate" hrefLang="x-default" href={siteUrl('en', path)} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={altLocale} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {noindex && <meta name="robots" content="noindex,follow" />}
      {!noindex && <meta name="robots" content="index,follow,max-image-preview:large" />}
      {jsonLdArr.map((item, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(item) }}
        />
      ))}
    </>
  )
}
